// Belge Şablonları Yönetimi — Ayarlar → Belge Şablonları
// Şablonları belge tipine göre listele, ekle/düzenle/sil.
import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import { Button, FormField, EmptyState, Modal } from '../components/ui';
import DataTable from '../components/DataTable';

const DOC_TYPES = [
  { value: 'sales_quote',     label: 'Verilen Teklif' },
  { value: 'purchase_quote',  label: 'Alınan Teklif' },
  { value: 'sales_order',     label: 'Alınan Sipariş' },
  { value: 'purchase_order',  label: 'Verilen Sipariş' },
  { value: 'sales_waybill',   label: 'Satış İrsaliyesi' },
  { value: 'purchase_waybill',label: 'Alış İrsaliyesi' },
  { value: 'sales_invoice',   label: 'Satış Faturası' },
  { value: 'purchase_invoice',label: 'Alış Faturası' },
];

const emptyTemplate = {
  doc_type: 'sales_quote',
  name: '',
  is_default: false,
  paper_size: 'A4',
  orientation: 'portrait',
  font_size: 10,
  margin_mm: 15,
  header_html: '',
  footer_html: '',
  show_logo: true,
  show_account: true,
  show_notes: true,
  show_signature: false,
  primary_color: '#1d6ef5',
  extra_css: '',
};

export default function PrintTemplatesPage() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState('');
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/print-templates', { params: filterType ? { doc_type: filterType } : {} });
      setList(r.data?.success ? (r.data.data || []) : []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [filterType]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing({ ...emptyTemplate, doc_type: filterType || 'sales_quote' }); setError(''); };
  const openEdit = (row) => {
    setError('');
    setEditing({
      id: row.id,
      doc_type: row.doc_type,
      name: row.name || '',
      is_default: !!row.is_default,
      paper_size: row.paper_size || 'A4',
      orientation: row.orientation || 'portrait',
      font_size: parseFloat(row.font_size) || 10,
      margin_mm: parseFloat(row.margin_mm) || 15,
      header_html: row.header_html || '',
      footer_html: row.footer_html || '',
      show_logo: row.show_logo !== false && row.show_logo !== 0,
      show_account: row.show_account !== false && row.show_account !== 0,
      show_notes: row.show_notes !== false && row.show_notes !== 0,
      show_signature: !!row.show_signature,
      primary_color: row.primary_color || '#1d6ef5',
      extra_css: row.extra_css || '',
    });
  };

  const save = async () => {
    if (!editing.name.trim()) { setError('Ad zorunludur'); return; }
    setSaving(true); setError('');
    try {
      if (editing.id) await api.put('/print-templates/' + editing.id, editing);
      else            await api.post('/print-templates', editing);
      setEditing(null);
      await load();
    } catch (e) { setError(e.response?.data?.message || 'Kayıt başarısız'); }
    finally { setSaving(false); }
  };

  const remove = async (row) => {
    if (!window.confirm(`"${row.name}" şablonu silinsin mi?`)) return;
    try {
      await api.delete('/print-templates/' + row.id);
      await load();
    } catch (e) { alert(e.response?.data?.message || 'Silme başarısız'); }
  };

  return (
    <div className="stocks-page">
      <div className="stocks-topbar">
        <div className="stocks-topbar-left">
          <h2>Belge Şablonları</h2>
          <span className="stocks-count-badge">{list.length} şablon</span>
        </div>
        <div className="stocks-topbar-right">
          <select value={filterType} onChange={e => setFilterType(e.target.value)}
            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border-input)', background: 'var(--bg-input)' }}>
            <option value="">Tüm Belge Tipleri</option>
            {DOC_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <Button size="sm" onClick={openNew}>+ Yeni Şablon</Button>
        </div>
      </div>

      <div className="stocks-table-wrap">
        <DataTable
          pageKey="print-templates-list"
          tableClassName="stocks-table"
          rowClassName="stocks-table-row"
          loading={loading}
          data={list}
          rowKey="id"
          onRowClick={openEdit}
          emptyText=""
          columns={[
            { id: 'name', header: 'Şablon Adı', accessor: 'name', sortable: true, width: 240,
              cell: r => <strong className="stock-code">{r.name}</strong> },
            { id: 'doc_type', header: 'Belge Tipi', accessor: 'doc_type', sortable: true, width: 180,
              cell: r => <span className="stock-secondary">{DOC_TYPES.find(d => d.value === r.doc_type)?.label || r.doc_type}</span> },
            { id: 'is_default', header: 'Varsayılan', accessor: 'is_default', sortable: true, width: 110,
              headerStyle: { textAlign: 'center' }, cellStyle: { textAlign: 'center' },
              cell: r => r.is_default ? <span style={{ background: 'rgba(22,163,74,0.12)', color: '#16a34a', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700 }}>✓ Varsayılan</span> : <span style={{ color: 'var(--text-tertiary)' }}>—</span> },
            { id: 'paper', header: 'Kağıt', sortable: true, width: 100,
              accessor: r => `${r.paper_size} ${r.orientation === 'portrait' ? 'D' : 'Y'}`,
              cell: r => <span style={{ fontFamily: 'monospace' }}>{r.paper_size} · {r.orientation === 'portrait' ? 'Dikey' : 'Yatay'}</span> },
            { id: 'created_at', header: 'Oluşturma', sortable: true, width: 140,
              accessor: 'created_at',
              cell: r => <span className="stock-secondary">{r.created_at ? new Date(r.created_at).toLocaleString('tr-TR') : ''}</span> },
            { id: 'actions', header: 'İşlem', sortable: false, hideable: false, width: 150,
              headerStyle: { textAlign: 'center' }, cellStyle: { textAlign: 'center' },
              cell: r => (
                <span onClick={e => e.stopPropagation()}>
                  <Button variant="ghost" size="sm" onClick={() => openEdit(r)}>Düzenle</Button>
                  {' '}
                  <Button variant="danger" size="sm" onClick={() => remove(r)}>Sil</Button>
                </span>
              ) },
          ]}
        />
        {!loading && list.length === 0 && (
          <div style={{ padding: 24 }}>
            <EmptyState title="Henüz şablon yok"
              description='Belge tipine göre özel şablonlar tanımlayın. "+ Yeni Şablon" ile başlayın.' compact />
          </div>
        )}
      </div>

      {editing && (
        <Modal
          title={editing.id ? `Şablon Düzenle: ${editing.name}` : 'Yeni Şablon'}
          size="lg"
          onClose={() => setEditing(null)}
          closeOnOverlay={false}
          primaryAction={{ label: 'Kaydet', onClick: save, loading: saving }}
          cancelAction={{ label: 'Vazgeç', onClick: () => setEditing(null) }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <FormField label="Şablon Adı *">
              <input type="text" value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })}
                placeholder="Örn: Standart Teklif" />
            </FormField>
            <FormField label="Belge Tipi *">
              <select value={editing.doc_type} onChange={e => setEditing({ ...editing, doc_type: e.target.value })}>
                {DOC_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </FormField>
            <FormField label="Kağıt Boyutu">
              <select value={editing.paper_size} onChange={e => setEditing({ ...editing, paper_size: e.target.value })}>
                <option value="A4">A4</option>
                <option value="A5">A5</option>
                <option value="Letter">Letter</option>
              </select>
            </FormField>
            <FormField label="Yönlendirme">
              <select value={editing.orientation} onChange={e => setEditing({ ...editing, orientation: e.target.value })}>
                <option value="portrait">Dikey</option>
                <option value="landscape">Yatay</option>
              </select>
            </FormField>
            <FormField label="Yazı Boyutu (pt)">
              <input type="number" step="0.5" min="6" max="20" value={editing.font_size}
                onChange={e => setEditing({ ...editing, font_size: parseFloat(e.target.value) || 10 })} />
            </FormField>
            <FormField label="Kenar Boşluğu (mm)">
              <input type="number" step="0.5" min="0" max="50" value={editing.margin_mm}
                onChange={e => setEditing({ ...editing, margin_mm: parseFloat(e.target.value) || 15 })} />
            </FormField>
            <FormField label="Vurgu Rengi">
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type="color" value={editing.primary_color}
                  onChange={e => setEditing({ ...editing, primary_color: e.target.value })}
                  style={{ width: 50, height: 32, padding: 0, border: '1px solid var(--border-secondary)', borderRadius: 4 }} />
                <input type="text" value={editing.primary_color}
                  onChange={e => setEditing({ ...editing, primary_color: e.target.value })}
                  style={{ flex: 1, fontFamily: 'monospace' }} />
              </div>
            </FormField>
            <FormField label="Görünüm Seçenekleri">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: '13px' }}>
                <label><input type="checkbox" checked={editing.show_logo}
                  onChange={e => setEditing({ ...editing, show_logo: e.target.checked })} /> Logo göster</label>
                <label><input type="checkbox" checked={editing.show_account}
                  onChange={e => setEditing({ ...editing, show_account: e.target.checked })} /> Cari bilgileri</label>
                <label><input type="checkbox" checked={editing.show_notes}
                  onChange={e => setEditing({ ...editing, show_notes: e.target.checked })} /> Notlar</label>
                <label><input type="checkbox" checked={editing.show_signature}
                  onChange={e => setEditing({ ...editing, show_signature: e.target.checked })} /> İmza alanları</label>
                <label><input type="checkbox" checked={editing.is_default}
                  onChange={e => setEditing({ ...editing, is_default: e.target.checked })} /> <strong>Varsayılan şablon</strong></label>
              </div>
            </FormField>
            <div style={{ gridColumn: '1 / -1' }}>
              <FormField label="Üst Bilgi (HTML, opsiyonel)" hint="Logo veya firma sloganı için HTML kullanabilirsiniz">
                <textarea rows={3} value={editing.header_html}
                  onChange={e => setEditing({ ...editing, header_html: e.target.value })}
                  placeholder='<img src="https://.../logo.png" height="40">'
                  style={{ width: '100%', fontFamily: 'monospace', fontSize: '12px' }} />
              </FormField>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <FormField label="Alt Bilgi (HTML, opsiyonel)" hint="İletişim, banka bilgileri, vb.">
                <textarea rows={3} value={editing.footer_html}
                  onChange={e => setEditing({ ...editing, footer_html: e.target.value })}
                  placeholder="Tel: ... · IBAN: ..."
                  style={{ width: '100%', fontFamily: 'monospace', fontSize: '12px' }} />
              </FormField>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <FormField label="Ek CSS (gelişmiş)" hint="Tüm stilleri özelleştirin (gerekirse)">
                <textarea rows={4} value={editing.extra_css}
                  onChange={e => setEditing({ ...editing, extra_css: e.target.value })}
                  placeholder=".grand { color: red; }"
                  style={{ width: '100%', fontFamily: 'monospace', fontSize: '12px' }} />
              </FormField>
            </div>
          </div>
          {error && <div className="ui-field-error" style={{ marginTop: 10, padding: 8, background: 'rgba(220,38,38,0.08)', borderRadius: 6 }}>{error}</div>}
        </Modal>
      )}
    </div>
  );
}
